import { createContext, useContext } from 'react';
import type { GardenElement, ShapeVertex } from './gardenReducer';

export interface GroupDragState {
  ids: string[];
  startPositions: Record<string, { x: number; y: number }>;
  dx: number;
  dy: number;
}

export interface GardenContextValue {
  elements: GardenElement[];
  shape: ShapeVertex[];
  selectedElementIds: string[];
  armedType: string | null;
  isEditingShape: boolean;
  groupDrag: GroupDragState | null;
  zoom: number;
  addElement: (elementType: string, x: number, y: number) => void;
  moveElement: (id: string, x: number, y: number) => void;
  resizeElement: (id: string, width: number, height: number) => void;
  renameElement: (id: string, name: string) => void;
  deleteElement: (id: string) => void;
  deleteElements: (ids: string[]) => void;
  addActionEntry: (elementId: string, date: string, note: string) => void;
  addActionEntryBulk: (elementIds: string[], date: string, note: string) => void;
  updateActionEntry: (elementId: string, entryId: string, date: string, note: string) => void;
  deleteActionEntry: (elementId: string, entryId: string) => void;
  selectElement: (id: string | null, isCtrl?: boolean) => void;
  clearSelection: () => void;
  armType: (type: string) => void;
  clearArmedType: () => void;
  toggleEditingShape: () => void;
  updateShapePoint: (id: string, x: number, y: number) => void;
  insertShapePoint: (index: number, x: number, y: number) => void;
  removeShapePoint: (id: string) => void;
  beginGroupDrag: (ids: string[]) => void;
  updateGroupDrag: (dx: number, dy: number) => void;
  commitGroupDrag: () => void;
  cancelGroupDrag: () => void;
  setZoom: (value: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
}

export const GardenContext = createContext<GardenContextValue | null>(null);

export function useGarden(): GardenContextValue {
  const context = useContext(GardenContext);
  if (!context) {
    throw new Error('useGarden must be used within a GardenProvider');
  }
  return context;
}
